/**
 * IngestionContext.tsx
 *
 * PURPOSE:
 * Shares ingestion state across the FileValidationModal without prop drilling.
 * Think of it as a "global state" scoped to the modal tree.
 *
 * WHAT THIS MANAGES:
 * ✓ Selected file (user picks a file to validate)
 * ✓ Ingestion record creation (POST to create DB entry)
 * ✓ URL persistence (so refreshing the page resumes the flow)
 * ✓ Error/loading states for the above
 *
 * WHAT THIS DOES NOT MANAGE:
 * ✗ Job polling (use SWR hooks like useIngestionJobs)
 * ✗ Validation results (fetched by individual step components)
 * ✗ Data profiling results (same as above)
 *
 * WHY CONTEXT (vs just hooks)?
 * - Multiple sibling components need the same state (file, ingestionId)
 * - Avoids passing props through 3+ component layers
 * - SWR is used for server data; Context is used for local UI state
 *
 * HOW TO USE:
 * 1. Wrap your component tree with <IngestionProvider>
 * 2. Call useIngestionContext() in any child component
 * 3. Access state (selectedFile, ingestionId) or actions (selectFile, startIngestion)
 *
 * @see ValidationSteps.tsx - uses this context for file display
 * @see ValidationNavigation.tsx - uses this context to trigger startIngestion
 */
import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useFileUpload } from "datasets/pages/tables/components/file-validation/hooks/useFileUpload";
import FileInfo from "files/types/interfaces/fileInfo";

const { VITE_CATALOG_API } = import.meta.env;

// =============================================================================
// Types
// =============================================================================

/** State values exposed by the context */
export interface IngestionState {
  /** Ingestion record ID from URL (e.g., ?ingestion=abc123) */
  ingestionId: string | null;
  /** The file user selected for validation (from cloud storage) */
  selectedFile: FileInfo | null;
  /** Local file pending upload (from user's device) */
  pendingLocalFile: File | null;
  /** True while creating an ingestion record */
  isLoading: boolean;
  /** Error message from the last failed operation */
  error: string | null;
  /** True while uploading a local file */
  isUploading: boolean;
  /** Upload progress percentage (0-100) */
  uploadProgress: number | undefined;
}

/** Actions (functions) exposed by the context */
export interface IngestionActions {
  /** Store a selected file in context (from cloud storage) */
  selectFile: (file: FileInfo) => void;
  /** Clear the selected file */
  clearFile: () => void;
  /** Store a local file pending upload (from user's device) */
  setPendingLocalFile: (file: File | null) => void;
  /** Upload pending local file to cloud storage. Returns FileInfo on success. */
  uploadPendingFile: () => Promise<FileInfo | null>;
  /** Create an ingestion record and persist ID to URL. Returns ID on success. */
  startIngestion: () => Promise<string | null>;
  /** Dismiss the current error */
  clearError: () => void;
  /** Reset in-memory state (file, error, loading). URL params are preserved. */
  reset: () => void;
}

/** Combined context value: state + actions + derived helpers */
export interface IngestionContextValue extends IngestionState, IngestionActions {
  /** True if ingestionId exists (either from URL or newly created) */
  hasIngestion: boolean;
  /** True if validation can proceed (has file, pending file, or existing ingestion) */
  canStartValidation: boolean;
  /** True if pending local file was successfully uploaded */
  isUploaded: boolean;
}

// =============================================================================
// Context Setup
// =============================================================================

const IngestionContext = createContext<IngestionContextValue | null>(null);

/**
 * Hook to access ingestion state and actions.
 * Must be called within an <IngestionProvider>.
 *
 * @example
 * const { selectedFile, startIngestion } = useIngestionContext();
 */
export const useIngestionContext = () => {
  const context = useContext(IngestionContext);
  if (!context) {
    throw new Error("useIngestionContext must be used within an IngestionProvider");
  }
  return context;
};

// =============================================================================
// Provider Component
// =============================================================================

interface IngestionProviderProps {
  children: ReactNode;
}

/**
 * Wraps the modal component tree and provides ingestion state to all children.
 * Place this at the root of the FileValidationModal.
 *
 * @example
 * <IngestionProvider>
 *   <FileValidationModalContent />
 * </IngestionProvider>
 */
export const IngestionProvider = ({ children }: IngestionProviderProps) => {
  // ---------------------------------------------------------------------------
  // Router hooks: read dataset ID from URL path, ingestion ID from query params
  // ---------------------------------------------------------------------------
  const { datasetId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---------------------------------------------------------------------------
  // Upload hook: handles file upload to cloud storage
  // ---------------------------------------------------------------------------
  const { uploadState, uploadFile, resetUpload, uploadError } = useFileUpload();

  // ---------------------------------------------------------------------------
  // Local state
  // ---------------------------------------------------------------------------
  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null);
  const [pendingLocalFile, setPendingLocalFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read ingestion ID from URL (persists across page refresh)
  const ingestionId = searchParams.get("ingestion");

  // ---------------------------------------------------------------------------
  // Actions (wrapped in useCallback to maintain stable references)
  // ---------------------------------------------------------------------------
  const selectFile = useCallback((file: FileInfo) => {
    setSelectedFile(file);
    setError(null); // Clear any previous error when user selects a new file
  }, []);

  const clearFile = useCallback(() => {
    setSelectedFile(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Uploads the pending local file to cloud storage.
   * After upload, the file becomes the selectedFile and pendingLocalFile is cleared.
   *
   * @returns FileInfo for the uploaded file, or null if upload failed
   */
  const uploadPendingFile = useCallback(async (): Promise<FileInfo | null> => {
    if (!pendingLocalFile) {
      setError("No file selected for upload");
      return null;
    }

    try {
      const uploadedFileInfo = await uploadFile(pendingLocalFile);
      setSelectedFile(uploadedFileInfo);
      setPendingLocalFile(null);
      return uploadedFileInfo;
    } catch {
      setError(uploadError || "Upload failed");
      return null;
    }
  }, [pendingLocalFile, uploadFile, uploadError]);

  /**
   * Creates a new ingestion record in the database.
   *
   * Flow:
   * 1. POST to /datasets/{datasetId}/ingestions (no file required)
   * 2. Receive ingestion ID from the backend
   * 3. Store ID in URL query params (for page refresh persistence)
   * 4. Return the ID so caller can proceed to next step
   *
   * NOTE: After SCDM-2478, ingestions are created WITHOUT source_file.
   * The file is passed to individual jobs via their arguments instead.
   *
   * @returns The new ingestion ID, or null if creation failed
   */
  const startIngestion = useCallback(async (): Promise<string | null> => {
    // Guard: need dataset context
    if (!datasetId) {
      setError("Dataset ID is required");
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call the ingestion API - no source_file needed (SCDM-2478)
      const response = await fetch(`${VITE_CATALOG_API}/datasets/${datasetId}/ingestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create ingestion: ${errorText}`);
      }

      // Parse response - backend returns plain text with quotes, strip them
      let newIngestionId = await response.text();
      newIngestionId = newIngestionId.replace(/^"|"$/g, "");

      // Persist to URL so page refresh doesn't lose progress.
      // Remove validated flag — this is a brand-new ingestion that hasn't been validated yet.
      const newParams = new URLSearchParams(searchParams);
      newParams.set("ingestion", newIngestionId);
      newParams.delete("validated");
      setSearchParams(newParams, { replace: true });

      return newIngestionId;
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : "Failed to start ingestion";
      console.error("Failed to start ingestion:", err);
      setError(errorMsg);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [datasetId, searchParams, setSearchParams]);

  /**
   * Resets in-memory context state to initial values.
   * URL params (ingestion, validated) are NOT cleared here — they persist
   * so the user can resume validation after closing and reopening the modal.
   * URL cleanup only happens on page unmount (navigating away from Add Table).
   */
  const reset = useCallback(() => {
    setSelectedFile(null);
    setPendingLocalFile(null);
    setError(null);
    setIsLoading(false);
    resetUpload();
  }, [resetUpload]);

  // ---------------------------------------------------------------------------
  // Derived values (computed from state, no setter needed)
  // ---------------------------------------------------------------------------
  const hasIngestion = !!ingestionId;
  const canStartValidation = !!selectedFile || !!pendingLocalFile || hasIngestion;
  const isUploaded = uploadState.uploadProgress === 100;

  // ---------------------------------------------------------------------------
  // Build the context value object
  // ---------------------------------------------------------------------------
  const value = useMemo<IngestionContextValue>(
    () => ({
      // State (read-only from consumer perspective)
      ingestionId,
      selectedFile,
      pendingLocalFile,
      isLoading,
      error: error || uploadError,
      isUploading: uploadState.isUploading,
      uploadProgress: uploadState.uploadProgress,

      // Derived helpers
      hasIngestion,
      canStartValidation,
      isUploaded,

      // Actions (call these to update state)
      selectFile,
      clearFile,
      setPendingLocalFile,
      uploadPendingFile,
      startIngestion,
      clearError,
      reset,
    }),
    [
      ingestionId,
      selectedFile,
      pendingLocalFile,
      isLoading,
      error,
      uploadError,
      uploadState.isUploading,
      uploadState.uploadProgress,
      hasIngestion,
      canStartValidation,
      isUploaded,
      selectFile,
      clearFile,
      setPendingLocalFile,
      uploadPendingFile,
      startIngestion,
      clearError,
      reset,
    ]
  );

  // ---------------------------------------------------------------------------
  // Render: wrap children with the context provider
  // ---------------------------------------------------------------------------
  return <IngestionContext.Provider value={value}>{children}</IngestionContext.Provider>;
};

export default IngestionContext;
